// src/hook.ts
import * as React from "react";
import { useSafeEffect } from "@stepstone-genesis/hooks-use-safe-effect";
import { isBrowser } from "@stepstone-genesis/hooks-use-ssr";
import { warn } from "@stepstone-genesis/utilities";
var warnings = {
  OUT_OF_RANGE: {
    message: "`initialBreakpoint` provided is out of range of breakpoints array provided. Please make sure that the `initialBreakpoint` is greater then 0 and not larger then your breakpoints array.",
    condition: true
  },
  SAME_LENGTH: {
    message: "`breakpoints` and `keys` need to have the same length. Please remember that `0` also needs a key. This means that `keys` length needs to be one greater then `breakpoints` length",
    condition: true
  },
  INITIAL_NUMBER_CHECK: {
    message: "`initialBreakpoint` must be a number",
    condition: true
  }
};
var createMediaQuery = (currentBreakpoint, nextBreakpoint) => {
  return `(min-width: ${currentBreakpoint}px) ${nextBreakpoint ? `and (max-width: ${nextBreakpoint - 1}px)` : ""}`;
};
var getActiveBreakpoint = (breakpoints) => {
  const activeBreakpoint = breakpoints.map((currentBreakpoint, i) => {
    const nextBreakpoint = breakpoints[i + 1];
    return window.matchMedia(
      createMediaQuery(currentBreakpoint, nextBreakpoint)
    ).matches;
  }).findIndex((breakpoint) => breakpoint);
  return activeBreakpoint;
};
var convertBreakpointsToMediaQueries = (breakpoints) => breakpoints.map((currentBreakpoint, i) => {
  const nextBreakpoint = breakpoints[i + 1];
  return window.matchMedia(
    createMediaQuery(currentBreakpoint, nextBreakpoint)
  );
});
var breakpointKeysToActiveMediaQuery = ({
  breakpoints,
  keys,
  isActive
}) => breakpoints.reduce((breakpoint, _, index) => {
  breakpoint[keys[index]] = index === isActive;
  return breakpoint;
}, {});
var useActiveMediaQuery = ({
  breakpoints,
  // manually set to our breakpoints. We get these in ResponsiveMediaProvider export.
  initialBreakpoint = 0,
  // fallback for SSR
  keys
}) => {
  const { current: bps } = React.useRef([0, ...breakpoints]);
  const { current: bpsKeys } = React.useRef(keys);
  const isSupported = isBrowser() && "matchMedia" in window;
  if (initialBreakpoint > breakpoints.length || initialBreakpoint < 0)
    warn(warnings.OUT_OF_RANGE);
  if (bps.length !== keys.length) warn(warnings.SAME_LENGTH);
  if (typeof initialBreakpoint !== "number")
    warn(warnings.INITIAL_NUMBER_CHECK);
  const initialState = React.useMemo(
    () => ({
      isActive: initialBreakpoint,
      isActiveKey: bpsKeys[initialBreakpoint],
      matches: breakpointKeysToActiveMediaQuery({
        breakpoints: bps,
        isActive: initialBreakpoint,
        keys: bpsKeys
      }),
      keys: bpsKeys
    }),
    [initialBreakpoint, bpsKeys, bps]
  );
  const [activeMediaQuery, setActiveMediaQuery] = React.useState(initialState);
  const listenForResponsiveChange = React.useCallback(
    (index) => (event) => {
      if (!event.matches) return;
      setActiveMediaQuery({
        isActive: index,
        isActiveKey: bpsKeys[index],
        matches: breakpointKeysToActiveMediaQuery({
          breakpoints: bps,
          isActive: index,
          keys: bpsKeys
        }),
        keys: bpsKeys
      });
    },
    [setActiveMediaQuery, bps, bpsKeys]
  );
  useSafeEffect(() => {
    if (!isSupported) return setActiveMediaQuery(initialState);
    const activeBreakpoint = getActiveBreakpoint(bps);
    setActiveMediaQuery({
      isActive: activeBreakpoint,
      isActiveKey: bpsKeys[activeBreakpoint],
      matches: breakpointKeysToActiveMediaQuery({
        breakpoints: bps,
        isActive: activeBreakpoint,
        keys: bpsKeys
      }),
      keys: bpsKeys
    });
    const listeners = [];
    const mediaQueries = convertBreakpointsToMediaQueries(bps);
    mediaQueries.forEach((mediaQuery, index) => {
      const listener = listenForResponsiveChange(index);
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", listener, { passive: true });
        listeners.push(listener);
      } else {
        mediaQuery.addListener(listener);
      }
    });
    return () => {
      mediaQueries.forEach((mediaQuery, index) => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", listeners[index]);
        } else {
          mediaQuery.removeListener(listeners[index]);
        }
      });
    };
  }, [bps, listenForResponsiveChange, bpsKeys, isSupported, initialState]);
  return activeMediaQuery;
};
export {
  useActiveMediaQuery
};
